/**
 * ============================================================================
 * SWITCH/TOGGLE COMPONENT - 100% Ready with Animations
 * ============================================================================
 */

import React, { forwardRef } from 'react';

/**
 * Switch Component
 * @param {Object} props
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional classes
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.id - Switch ID
 * @param {string} props.name - Switch name
 * @param {string} props.size - Size (sm, md, lg)
 * @param {string} props.variant - Variant (default, success, danger)
 * @returns {JSX.Element}
 */
const Switch = forwardRef(
  (
    {
      checked,
      onChange,
      label,
      error,
      className = '',
      disabled = false,
      id,
      name,
      size = 'md',
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: {
        track: 'w-9 h-5',
        thumb: 'w-4 h-4',
        translate: 'translate-x-4',
      },
      md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5',
        translate: 'translate-x-5',
      },
      lg: {
        track: 'w-14 h-7',
        thumb: 'w-6 h-6',
        translate: 'translate-x-7',
      },
    };

    const variantClasses = {
      default: checked
        ? 'bg-blue-600 dark:bg-blue-500'
        : 'bg-gray-400 dark:bg-white/35 dark:border-2 dark:border-white/50',
      success: checked
        ? 'bg-[#22c55e] dark:bg-[#22c55e]'
        : 'bg-gray-400 dark:bg-white/35 dark:border-2 dark:border-white/50',
      danger: checked
        ? 'bg-red-600 dark:bg-red-500'
        : 'bg-gray-400 dark:bg-white/35 dark:border-2 dark:border-white/50',
    };

    const sizes = sizeClasses[size] || sizeClasses.md;
    const switchId = id || name || `switch-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={`flex flex-col ${className}`}>
        <label
          htmlFor={switchId}
          className={`
            flex items-center gap-3 cursor-pointer
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {/* Switch */}
          <div className="relative flex-shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={switchId}
              name={name}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              className="sr-only"
              aria-invalid={!!error}
              aria-describedby={error ? `${switchId}-error` : undefined}
              {...props}
            />
            <div
              role="switch"
              aria-checked={checked}
              tabIndex={0}
              className={`
                ${sizes.track}
                ${variantClasses[variant] || variantClasses.default}
                rounded-full
                transition-colors duration-200
                focus:outline-none focus-within:ring-4 focus-within:ring-green-500/40
                ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                min-w-0
              `}
              onClick={() => !disabled && onChange?.({ target: { checked: !checked } })}
              onKeyDown={(e) => {
                if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
                  e.preventDefault();
                  onChange?.({ target: { checked: !checked } });
                }
              }}
            >
              <span
                className={`
                  ${sizes.thumb}
                  absolute top-0.5 left-0.5
                  bg-white rounded-full
                  transition-transform duration-200 ease-in-out
                  shadow-lg
                  border-2 border-gray-300 dark:border-white/60
                  ${checked ? sizes.translate : 'translate-x-0'}
                `}
              />
            </div>
          </div>

          {/* Label */}
          {label && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {label}
            </span>
          )}
        </label>

        {/* Error */}
        {error && (
          <p
            id={`${switchId}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
